import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { StatefulService } from './stateful.service';
import { PageInstance } from '@workflow/class/page.service';
import { pageNameMap } from './page-name-map.const';
import { SawDust } from '@cudirect/sawdust/sawdust';

export interface GAMetadata {
  Page: string;
  ApplicationType: string;
  ProductType: string;
  ProductSubType: string;
  ArcDXApplicationID: PrimitiveType;
  ApplicationID: string;
  isCoApplicant: boolean;
  isMember: PrimitiveType;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsMetadataService {
  public statefulService = inject(StatefulService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger = inject(SawDust);
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'AnalyticsMetadataService', ...additional };
  }
  private flowType = '';
  private type = '';
  private subType = '';
  private entityId: PrimitiveType;
  private isMember: PrimitiveType = null;

  /**
   * Returns the metadata for GA Events.
   * @param page The current PageInstance
   * @returns The GAMetadata object
   */
  public getMetadata(page: PageInstance): [string, GAMetadata] {
    if (!page) {
      this.logger.warn('Page is null or undefined. Returning default metadata.', this.logContext());
      return [
        'arcdx.unknown',
        {
          Page: '',
          ApplicationType: '',
          ProductType: '',
          ProductSubType: '',
          ArcDXApplicationID: null,
          ApplicationID: null,
          isCoApplicant: null,
          isMember: null
        }
      ];
    }


    // Flow type will determine if its a arcdx.loans or arcdx.accounts
    let flowType = this.isBrowser ? JSON.parse(sessionStorage.getItem('BMP.FlowType')) : null;

    if (!flowType) {
      flowType = this.inferFlowType(page);
    }
    // If the flow has changed, reset the values.
    if (this.flowType !== flowType || !this.type || (this.flowType === 'OLA' && !this.subType)) {
      this.verifyCurrentData(flowType);
    }
    this.logger.debug(
      'Resolved analytics metadata context',
      this.logContext({ flowType: this.flowType, type: this.type, subType: this.subType })
    );

    // Fix the page ID to not include a number
    // Determine if last character is a number. If it is, replace the number with a # to match the map.
    let fixedId = page.id;
    let applicantNumber = null;
    if (/\d$/.test(page.id)) {
      applicantNumber = page.id.slice(-1);
      fixedId = page.id.replace(/\d$/, '#');
    }
    // Check if the page ID is in the map. If not, use the original page ID.
    let pageName = pageNameMap[fixedId] || page.id;
    if (pageName === page.id) {
      this.logger.warn('Page name not found in analytics map', this.logContext({ pageId: page.id }));
    }

    // If we're in a coapplicant flow, append the coapplicant label
    if (applicantNumber) {
      pageName += '-co-app';
      switch (applicantNumber.toString()) {
        case '0':
          pageName += '-first';
          break;
        case '1':
          pageName += '-second';
          break;
        case '2':
          pageName += '-third';
          break;
        default:
      }
    }

    let actionName = 'arcdx.';
    let ApplicationID = '';

    if (flowType === 'OLA') {
      actionName += 'loans.';
      ApplicationID = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueString;
    } else if (flowType === 'OAO') {
      actionName += 'accounts.';
      ApplicationID = this.statefulService.wrap('api:oaoMemberInformation/accountsAndServices/applicationId').valueString;
    } else {
      actionName += 'unselected.';
    }

    actionName += pageName;

    const isCoApplicant = page?.id?.includes('co-app') || page?.id?.includes('co-borrower') || applicantNumber;

    return [
      actionName,
      {
        Page: page.title,
        ApplicationType: this.flowType, // Flow
        ProductType: this.type || null, // Auto vs. Personal
        ProductSubType: this.subType || null, // Auto new vs Auto Used
        ArcDXApplicationID: this.entityId,
        ApplicationID: ApplicationID || null,
        isCoApplicant,
        isMember: this.isMember || null // Is the user a member?
      }
    ];
  }

  /**
   * Sets the metadata values based on the flow type.
   * @param flowType The flow type (e.g., 'OLA' or 'OAO').
   */
  private verifyCurrentData(flowType: string): void {
    this.flowType = flowType;

    const isOla = flowType === 'OLA';
    const typestorage = isOla ? 'ola:olaApplicationInformation/loanGroupName' : 'api:oaoMemberInformation/membership/membershipName';
    const subTypeStorage = isOla ? 'ola:olaApplicationInformation/loanProductName' : '';

    this.type = this.statefulService.wrap(typestorage).valueStringOnly;
    if (subTypeStorage) {
      this.subType = this.statefulService.wrap(subTypeStorage).valueStringOnly;
    }
    this.entityId = this.statefulService.wrap('api:entityId').value;

    if (isOla) {
      this.isMember = this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value;
    } else {
      this.isMember = this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value;
    }

    this.logger.debug(
      'Updated analytics metadata cache',
      this.logContext({ flowType, type: this.type, subType: this.subType, entityId: this.entityId })
    );
  }

  /**
   * Public method to bust the cache and reset the values.
   * Called when user switches loan types.
   */
  public resetValues(): void {
    this.flowType = null;
    // The next call will reset the variables.
    this.logger.info('Reset analytics metadata cache', this.logContext());
  }

  private inferFlowType(page: PageInstance): string {
    // Infer the flow type based on the page ID
    if (page?.id?.startsWith('ola')) {
      return 'OLA';
    } else if (page?.id?.startsWith('oao')) {
      return 'OAO';
    } else if (page?.id?.includes('econsent')) {
      // ola-econsent would be caught above, so a fallthru means OAO
      // We should really rename the page in WW to have the correct prefix.
      return 'OAO';
    } else {
      this.logger.debug('Unable to determine flow type from page; defaulting to empty string', this.logContext({ pageId: page?.id }));
      return ''; // Default case
    }
  }
}
